import { useState, useEffect, useCallback } from 'react';
import { chatbotApi } from '../services/chatbotApi';
import { useTranslation } from 'react-i18next';

const useChatbot = (sessionId, sessionType = 'general') => {
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState(() => {
    if (!sessionId) return [];
    try {
      const storedMessages = sessionStorage.getItem(`chatbotMessages_${sessionId}`);
      return storedMessages ? JSON.parse(storedMessages) : [];
    } catch (error) {
      console.error("Could not parse chatbot messages from sessionStorage", error);
      return [];
    }
  });
  const [isLoading, setIsLoading] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [isSessionLoading, setIsSessionLoading] = useState(false);
  const [availableActions, setAvailableActions] = useState([]);
  const [error, setError] = useState(null);

  // Effect to clear messages when session changes
  useEffect(() => {
    setMessages([]);
  }, [sessionId]);

  // Effect to save messages to sessionStorage
  useEffect(() => {
    if (!sessionId) return;
    try {
      // Avoid saving the initial empty array before messages are loaded/added
      if (messages.length > 0) {
        sessionStorage.setItem(`chatbotMessages_${sessionId}`, JSON.stringify(messages));
      }
    } catch (error) {
      console.error("Could not save chatbot messages to sessionStorage", error);
    }
  }, [messages, sessionId]);

  // Load session data
  const loadSession = useCallback(async () => {
    if (!sessionId) return;

    try {
      setIsSessionLoading(true);
      setError(null);

      
      const sessionData = await chatbotApi.getSession(sessionId);
      setSession(sessionData);
      
      // Load messages from API. This will overwrite the session-restored messages
      // but ensures the UI is in sync with the backend "source of truth".
      const messagesData = await chatbotApi.getSessionMessages(sessionId);
      setMessages(Array.isArray(messagesData) ? messagesData : []);
      
      // Load available actions from new agentic system
      const actionsData = await chatbotApi.getAvailableActions(sessionId);
      if (actionsData.agentic && Array.isArray(actionsData.agentic)) {
        setAvailableActions(actionsData.agentic);
      } else {
        // Fallback to default actions
        setAvailableActions([
          'Add a new candidate',
          'Upload a resume and extract candidate info',
          'List all candidates',
          'Analyze resume skills',
          'Find job matches'
        ]);
      }
      
    } catch (err) {
      console.error('Error loading session:', err);
      setError(err.message || 'Failed to load session');
    } finally {
      setIsSessionLoading(false);
    }
  }, [sessionId]);

  // Send message to agentic AI
  const sendMessage = useCallback(async (content, actionType = null, actionData = {}) => {
    if (!sessionId) {
      throw new Error('No session ID provided');
    }

    try {
      setError(null);

      // Add user message to local state FIRST
      const userMessage = {
        message_type: 'user',
        content,
        action_type: actionType,
        action_data: actionData,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, userMessage]);

      // Now set loading state (shows interactive engagement banner without creating an empty chat bubble!)
      setIsLoading(true);

      const botMessageId = 'bot_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      let botMessageAdded = false;

      const messageData = {
        content,
        action_data: actionData,
        language: currentLanguage
      };
      
      // Only include action_type if it's provided
      if (actionType) {
        messageData.action_type = actionType;
      }
      
      const response = await new Promise((resolve, reject) => {
        let fullContent = '';
        let metadata = {};

        const ensureBotMessage = (initialContent = '') => {
          if (!botMessageAdded) {
            botMessageAdded = true;
            setIsLoading(false); // Hide engagement loader once streaming response begins
            const newBotMessage = {
              id: botMessageId,
              message_type: 'bot',
              content: initialContent,
              action_type: metadata.action_type || metadata.action || null,
              action_data: metadata.action_data || metadata.data || {},
              suggestions: metadata.suggestions || [],
              download_url: metadata.download_url,
              intent: metadata.intent,
              plan: metadata.plan,
              matches: metadata.matches,
              skills: metadata.skills,
              timestamp: new Date().toISOString()
            };
            setMessages(prev => [...prev, newBotMessage]);
          }
        };

        chatbotApi.sendMessageStream(
          sessionId,
          messageData,
          (tokenText) => {
            fullContent += tokenText;
            if (!botMessageAdded) {
              ensureBotMessage(fullContent);
            } else {
              setMessages(prev => prev.map(msg => 
                msg.id === botMessageId ? { ...msg, content: fullContent } : msg
              ));
            }
          },
          (meta) => {
            metadata = { ...metadata, ...meta };
            if (meta.suggestions) {
              setAvailableActions(prev => [...new Set([...prev, ...meta.suggestions])]);
            }
            if (botMessageAdded) {
              setMessages(prev => prev.map(msg => 
                msg.id === botMessageId ? {
                  ...msg,
                  action_type: meta.action_type || meta.action || msg.action_type,
                  action_data: meta.action_data || meta.data || msg.action_data,
                  suggestions: meta.suggestions || msg.suggestions,
                  download_url: meta.download_url || msg.download_url,
                  intent: meta.intent || msg.intent,
                  plan: meta.plan || msg.plan,
                  matches: meta.matches || msg.matches,
                  skills: meta.skills || msg.skills
                } : msg
              ));
            }
          },
          (err) => {
            reject(new Error(err));
          },
          (doneData) => {
            if (!botMessageAdded) {
              ensureBotMessage(fullContent || doneData.message || '');
            }
            resolve({
              content: fullContent,
              ...metadata,
              ...doneData
            });
          }
        );
      });

      return response;

    } catch (err) {
      console.error('Error sending message:', err);
      setError(err.message || 'Failed to send message');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, currentLanguage]);

  // Execute action using agentic AI
  const executeAction = useCallback(async (actionType, actionData = {}) => {
    if (!sessionId) {
      throw new Error('No session ID provided');
    }

    try {
      setIsLoading(true);
      setError(null);

      // Convert action to natural language for agentic AI
      const actionMessage = `Execute action: ${actionType} with data: ${JSON.stringify(actionData)}`;
      const response = await sendMessage(actionMessage);

      return response;

    } catch (err) {
      console.error('Error executing action:', err);
      setError(err.message || 'Failed to execute action');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, sendMessage]);

  // End session
  const endSession = useCallback(async () => {
    if (!sessionId) return;

    try {
      await chatbotApi.endSession(sessionId);
      sessionStorage.removeItem(`chatbotMessages_${sessionId}`);
      setSession(null);
      setMessages([]);
      setAvailableActions([]);
    } catch (err) {
      console.error('Error ending session:', err);
      setError(err.message || 'Failed to end session');
      throw err;
    }
  }, [sessionId]);

  // Load session on mount
  useEffect(() => {
    if (sessionId && messages.length === 0) {
      if (sessionId){
        // Only load if messages aren't already populated from session storage
        loadSession();
      } 
    }
  }, [sessionId, loadSession, messages.length]);

  // Add message directly to chat (for file uploads, etc.)
  const addMessage = useCallback((messageType, content, actionType = null, actionData = {}, suggestions = null) => {
    const message = {
      message_type: messageType,
      content,
      action_type: actionType,
      action_data: actionData,
      suggestions: suggestions,
      timestamp: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, message]);
  }, []);

  // When chat is reset
  const resetChat = useCallback(() => {
  setMessages([]);
  setIsLoading(false);
  setAvailableActions([]);
  setError(null);
}, []);

  return {
    session,
    messages,
    isLoading,
    availableActions,
    error,
    sendMessage,
    executeAction,
    addMessage,
    endSession,
    loadSession,
    resetChat
  };
};

export { useChatbot };